// TabFeatures.jsx - Modular Feature Activator for MeMark
// Keeps MeMark minimalist and clean by default, allowing users to toggle on only what they need.

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FEATURE_MODULES, getEnabledFeatures, saveEnabledFeatures } from '../../lib/featureFlags';
import { playSound } from '../../lib/soundEffects';

const { FiCheck, FiSliders, FiZap, FiX } = FiIcons;

export default function TabFeatures({ theme, accent = '#10B981' }) {
  const { textColor, mutedText, surfaceBg, cardBg, inputBorder } = theme;
  const [features, setFeatures] = useState(() => getEnabledFeatures());

  const handleToggle = (id) => {
    playSound('click');
    const next = { ...features, [id]: !features[id] };
    setFeatures(next);
    saveEnabledFeatures(next);
  };

  const handleEnableAll = () => {
    playSound('launch');
    const next = {};
    FEATURE_MODULES.forEach((m) => { next[m.id] = true; });
    setFeatures(next);
    saveEnabledFeatures(next);
  };

  const handleDisableAll = () => {
    playSound('click');
    const next = {};
    FEATURE_MODULES.forEach((m) => { next[m.id] = false; });
    setFeatures(next);
    saveEnabledFeatures(next);
  };

  const enabledCount = Object.values(features).filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Top Overview & Master Controls */}
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between p-4.5 rounded-2xl border gap-4"
        style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}
      >
        <div className="flex items-center gap-3.5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
            style={{ backgroundColor: `${accent}20`, border: `1px solid ${accent}40` }}
          >
            <SafeIcon icon={FiSliders} className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold" style={{ color: textColor }}>
                Modular Extensions &amp; Labs
              </h3>
              <span
                className="text-[10px] font-mono px-2 py-0.5 rounded-full font-bold"
                style={{
                  backgroundColor: enabledCount > 0 ? `${accent}25` : 'rgba(255,255,255,0.06)',
                  color: enabledCount > 0 ? accent : mutedText,
                  border: `1px solid ${enabledCount > 0 ? `${accent}50` : 'transparent'}`,
                }}
              >
                {enabledCount} / {FEATURE_MODULES.length} Active
              </span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: mutedText }}>
              All extra functions are disabled by default for maximum minimalism. Toggle on tools as needed.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={handleDisableAll}
            className={`px-3 py-1.5 rounded-xl border text-[11px] font-semibold transition-all cursor-pointer ${
              theme.isDark
                ? 'text-zinc-400 hover:text-white hover:bg-white/5 border-white/10'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 border-slate-300'
            }`}
            title="Reset to default clean minimalist state"
          >
            Minimal (Default)
          </button>
          <button
            type="button"
            onClick={handleEnableAll}
            className="px-3 py-1.5 rounded-xl text-[11px] font-semibold text-black transition-all cursor-pointer shadow-sm hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Enable All
          </button>
        </div>
      </div>

      {/* Feature Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {FEATURE_MODULES.map((mod) => {
          const isEnabled = !!features[mod.id];
          const IconComp = FiIcons[mod.iconName] || FiZap;

          return (
            <motion.div
              key={mod.id}
              whileHover={{ y: -1 }}
              className={`p-4 rounded-2xl border transition-all flex items-start justify-between gap-3 ${
                isEnabled
                  ? 'border-emerald-500/40 bg-emerald-500/[0.04] shadow-[0_4px_20px_rgba(16,185,129,0.08)]'
                  : theme.isDark
                  ? 'border-white/10 hover:border-white/20'
                  : 'border-slate-200 hover:border-slate-300 shadow-xs'
              }`}
              style={{ backgroundColor: isEnabled ? undefined : cardBg }}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                    isEnabled
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-xs'
                      : theme.isDark
                      ? 'bg-white/5 text-zinc-500 border border-white/5'
                      : 'bg-slate-100 text-slate-500 border border-slate-200'
                  }`}
                >
                  <SafeIcon icon={IconComp} className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold truncate" style={{ color: isEnabled ? textColor : mutedText }}>
                      {mod.name}
                    </span>
                    <span
                      className="text-[9px] font-mono px-1.5 py-0.2 rounded-full uppercase shrink-0"
                      style={{
                        backgroundColor: isEnabled ? `${accent}20` : theme.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                        color: isEnabled ? accent : mutedText,
                        border: `1px solid ${isEnabled ? `${accent}35` : 'transparent'}`,
                      }}
                    >
                      {mod.badge}
                    </span>
                  </div>
                  <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: mutedText }}>
                    {mod.description}
                  </p>
                </div>
              </div>

              {/* iOS-Style Animated Switch */}
              <button
                type="button"
                onClick={() => handleToggle(mod.id)}
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 p-0.5 focus:outline-none ${
                  isEnabled
                    ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.4)]'
                    : theme.isDark
                    ? 'bg-zinc-800 border border-zinc-700'
                    : 'bg-slate-300 border border-slate-300'
                }`}
                title={isEnabled ? 'Click to disable' : 'Click to enable'}
              >
                <motion.div
                  layout
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className={`w-5 h-5 rounded-full shadow-md flex items-center justify-center ${
                    isEnabled
                      ? 'bg-black text-emerald-400 ml-auto'
                      : theme.isDark
                      ? 'bg-zinc-400 text-zinc-900 ml-0'
                      : 'bg-white text-slate-400 ml-0 shadow-sm'
                  }`}
                >
                  {isEnabled && <SafeIcon icon={FiCheck} className="w-3 h-3 font-extrabold" />}
                </motion.div>
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
